import { useTextePublic } from '../../lib/textes-publics'
/**
 * REZ-02 — le compte à rebours du panier, avec son avertissement à 2 minutes.
 *
 * ## Deux horloges, et une seule fait foi
 *
 * Le serveur envoie `secondesRestantes`, PAS seulement `expireLe`. La raison
 * est terre à terre : l'horloge d'un téléphone dérive, celle d'un ordinateur de
 * bureau mal synchronisé dérive beaucoup. Un visiteur verrait « 12 minutes »
 * quand le serveur en compte 9, et perdrait ses places pendant que son écran le
 * rassure.
 *
 * Ce composant décompte donc **localement à partir de la valeur du serveur**, et
 * `usePanier` resynchronise toutes les minutes. Entre deux synchronisations,
 * l'affichage peut dériver de quelques dixièmes ; il ne peut pas dériver de
 * trois minutes.
 *
 * ## Ce qu'il fait à zéro
 *
 * Rien de brutal. Il prévient le parent (`onExpiration`), qui redemande le
 * panier au serveur : c'est LUI qui décide que le panier est expiré, jamais le
 * client. Un navigateur en avance de deux secondes ne doit pas pouvoir déclarer
 * perdues des places que le serveur tient encore.
 */

import { useEffect, useRef, useState } from 'react'

import { cn } from '../../lib/cn'

export interface CompteAReboursProps {
  /** Secondes restantes telles que le SERVEUR les a comptées. */
  readonly secondes: number
  /** REZ-02 — seuil de l'avertissement, en secondes (2 minutes par défaut). */
  readonly seuilAvertissement: number
  /** Appelé une fois quand le décompte atteint zéro. */
  readonly onExpiration?: (() => void) | undefined
  readonly className?: string | undefined
}

function format(secondes: number): string {
  const minutes = Math.floor(secondes / 60)
  const reste = secondes % 60
  return `${String(minutes)}:${String(reste).padStart(2, '0')}`
}

export function CompteARebours({
  secondes,
  seuilAvertissement,
  onExpiration,
  className,
}: CompteAReboursProps) {
  const texteInterface = useTextePublic()

  const [restant, setRestant] = useState(secondes)
  const [ancre, setAncre] = useState(secondes)
  const expirationSignalee = useRef(false)

  /**
   * Toute nouvelle réponse du serveur reprend la main sur le décompte local.
   *
   * Écrit PENDANT le rendu et non dans un `useEffect` : c'est le motif
   * « ajuster un état quand une prop change » de la documentation React. Un
   * effet afficherait une frame avec l'ancienne valeur avant de la corriger —
   * ici, une seconde de compte à rebours faux à chaque resynchronisation.
   */
  if (ancre !== secondes) {
    setAncre(secondes)
    setRestant(secondes)
    if (secondes > 0) expirationSignalee.current = false
  }

  /* Une seule minuterie pour toute la vie du composant : elle se contente de
     ne pas descendre sous zéro. La relancer à chaque seconde écoulée
     accumulerait une dérive et ferait clignoter le rendu. */
  useEffect(() => {
    const minuterie = setInterval(() => {
      setRestant((precedent) => (precedent <= 1 ? 0 : precedent - 1))
    }, 1000)
    return () => {
      clearInterval(minuterie)
    }
  }, [])

  useEffect(() => {
    if (restant > 0 || expirationSignalee.current) return
    expirationSignalee.current = true
    onExpiration?.()
  }, [restant, onExpiration])

  const alerte = restant <= seuilAvertissement

  return (
    <div
      className={cn(
        'inline-flex items-center gap-2 rounded-full px-3 py-1.5 text-sm font-medium tabular-nums',
        alerte ? 'bg-red-50 text-red-800 ring-1 ring-red-200' : 'bg-slate-100 text-slate-700',
        className,
      )}
      /* `polite` et non `assertive` : le minuteur ne doit pas couper la parole
         au lecteur d'écran toutes les secondes. L'annonce n'a lieu qu'au
         passage sous le seuil, via `aria-label` ci-dessous. */
      aria-live="polite"
      aria-atomic="true"
    >
      <svg viewBox="0 0 20 20" className="size-4 shrink-0" aria-hidden="true" fill="none">
        <circle cx="10" cy="10" r="7.5" stroke="currentColor" strokeWidth="1.6" />
        <path d="M10 6v4.2l2.6 1.6" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
      </svg>
      <span>
        {restant <= 0 ? (
          texteInterface('Panier expiré')
        ) : (
          <>
            <span className="sr-only">
              {alerte
                ? texteInterface('Attention : vos places sont réservées encore {temps} minutes.', {
                    temps: format(restant),
                  })
                : texteInterface('Places réservées encore {temps} minutes.', {
                    temps: format(restant),
                  })}
            </span>
            <span aria-hidden="true">{format(restant)}</span>
          </>
        )}
      </span>
    </div>
  )
}
